import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Chip,
  Box,
  TextField,
  Autocomplete,
  Snackbar,
  Alert,
} from "@mui/material";
import { Input } from "@/app/global-components/input";
import { Button } from "@/app/global-components/button";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { useLocale } from "@/middleware";
import { useTranslate } from "@/locales";
import useItems from "@/hooks/use-item";
import { unitOptions } from "@/consts";

const ItemPage: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const { id } = useParams<{ id: string }>();
  const [item, setItem] = useState<any | null>(null);
  const [updatedItem, setUpdatedItem] = useState<any | null>(null);
  const { fetchItem, updateItem, isLoading, error } = useItems();
  const [getItem, setGetItem] = useState<Boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const categories = useSelector((state: RootState) => state.auth.categories);

  // Notification state
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const showNotification = (
    message: string,
    severity: "success" | "error" | "warning" | "info"
  ) => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  useEffect(() => {
    const loadItem = async () => {
      if (!id) return;
      const fetchedItem = await fetchItem(id);
      setItem(fetchedItem?.data || null);
      setUpdatedItem({
        ...fetchedItem?.data,
        attachments: fetchedItem?.data?.attachments || [],
      });
      setGetItem(false);
    };

    loadItem();
  }, [id]);
  useEffect(() => {
    const loadItem = async () => {
      if (!id) return;
      if (getItem) {
        const fetchedItem = await fetchItem(id);
        setItem(fetchedItem?.data || null);
        // console.log(fetchedItem?.data)
        setUpdatedItem({
          ...fetchedItem?.data,
          attachments: fetchedItem?.data?.attachments || [],
        });
        setGetItem(false);
      }
    };

    loadItem();
  }, [getItem]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setUpdatedItem((prev: any) => ({
      ...prev,
      [name]: name === "price" || name === "vat" ? +value : value,
    }));
  };
  const OnchangeDescription = (Value: string) => {
    setUpdatedItem({
      ...updatedItem,
      description: Value,
    });
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUpdatedItem((prev: any) => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file),
      }));
    }
  };

  const handleAttachmentAdd = () => {
    setUpdatedItem((prev: any) => ({
      ...prev,
      attachments: [
        ...(prev?.attachments || []),
        { name: "", description: "", fileId: null },
      ],
    }));
  };

  const handleAttachmentRemove = (index: number) => {
    setUpdatedItem((prev: any) => {
      const updatedAttachments =
        prev?.attachments?.filter((_: any, i: number) => i !== index) || [];
      return { ...prev, attachments: updatedAttachments };
    });
  };

  const handleAttachmentChange = (
    index: number,
    field: string,
    value: string | File | null
  ) => {
    setUpdatedItem((prev: any) => {
      const updatedAttachments = [...(prev?.attachments || [])];
      updatedAttachments[index] = {
        ...updatedAttachments[index],
        [field]: value,
      };
      return { ...prev, attachments: updatedAttachments };
    });
  };
  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleSave = async () => {
    if (!updatedItem?.name) {
      showNotification(t("alerts.required.itemName"), "warning");
      return;
    }

    if (updatedItem.price < 0) {
      showNotification(t("alerts.error.negativePrice"), "error");
      return;
    }

    if (updatedItem) {
      const response = await updateItem(
        updatedItem
        // categories: selectedCategories, // Ensure tags are saved as JSON
      );
      if (response) {
        showNotification(t("alerts.success.itemUpdated"), "success");
        setGetItem(true);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <p className="text-xl text-gray-700">{t("status.loading")}</p>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="flex justify-center items-center h-screen text-gray-500">
        <p>{t("item.notFound")}</p>
      </div>
    );
  }

  // Show error notification if there's an error from the hook
  React.useEffect(() => {
    if (error) {
      showNotification(`${t("alerts.error.genericError")} ${error}`, "error");
    }
  }, [error]);

  return (
    <div
      className="flex flex-col min-h-screen bg-gray-50"
      dir={locale === "ar" ? "rtl" : "ltr"}
    >
      <div className="container mx-auto px-4 py-8" style={{ overflow: "auto" }}>
        <div className="flex justify-between items-right mb-6">
          <h1 className="text-2xl md:text-3xl lg:text-xl font-bold text-primary-dark">
            {item.name} #{item.ItemNR}
          </h1>
        </div>
        <Box className="bg-white p-6 rounded-lg shadow space-y-6">
          <Box className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Box className="grid grid-cols-12 space-y-4">
              <div className="col-span-12 space-y-1">
                <label className="block text-sm font-medium">
                  {t("item.fields.name")}
                </label>
                <Input
                  type="text"
                  name="name"
                  placeholder={t("item.fields.name")}
                  value={updatedItem?.name || ""}
                  onChange={handleInputChange}
                  className="w-full py-2 px-3"
                />
              </div>

              <div className="col-span-4 space-y-1  pl-2">
                <label className="block text-sm font-medium">
                  {t("item.fields.code")}
                </label>
                <Input
                  type="text"
                  name="code"
                  placeholder={t("item.placeholders.entercode")}
                  value={updatedItem?.code || ""}
                  onChange={handleInputChange}
                  className="w-full py-2 px-3"
                />
              </div>
              <div className="col-span-4 space-y-1  pl-2">
                <label className="block text-sm font-medium">
                  {t("item.fields.brand")}
                </label>
                <Input
                  type="text"
                  name="brand"
                  placeholder={t("item.placeholders.enterBrand")}
                  value={updatedItem?.brand || ""}
                  onChange={handleInputChange}
                  className="w-full py-2 px-3"
                />
              </div>
              <div className="col-span-4 space-y-1  pl-2">
                <label className="block text-sm font-medium">
                  {t("item.fields.model")}
                </label>
                <Input
                  type="text"
                  name="model"
                  placeholder={t("item.placeholders.enterModel")}
                  value={updatedItem?.model || ""}
                  onChange={handleInputChange}
                  className="w-full py-2 px-3"
                />
              </div>
              <div className="col-span-3 space-y-1 pl-2">
                <label className="block text-sm font-medium">
                  {t("item.fields.unit")}
                </label>
                <select
                  name="unit"
                  value={updatedItem?.unit || ""}
                  onChange={handleInputChange}
                  className="w-full py-2 px-3 border rounded"
                >
                  <option value={""}></option>
                  {unitOptions.map((unit) => (
                    <option key={unit.value} value={unit.value}>
                      {unit.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-12 space-y-1">
                <label className="block text-sm font-medium">
                  {t("item.fields.categories")}
                </label>
                <Autocomplete
                  multiple
                  options={categories
                    ?.filter((category) =>
                      myCompany.categoriesIds.includes(category?.id)
                    )
                    .map((category) => category.name)}
                  value={[...selectedCategories]}
                  onChange={(_, value) => {
                    setSelectedCategories(value);
                    setUpdatedItem((prev: any) => ({
                      ...prev,
                      categories: JSON.stringify(value), // Ensure categories are saved as JSON
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t("item.placeholders.enterCate")}
                      variant="outlined"
                      size="small"
                    />
                  )}
                  renderTags={(value, getTagProps) => {
                    const parsedValue =
                      typeof value === "string" ? JSON.parse(value) : value;
                    return parsedValue.map((option: string, index: number) => (
                      <Chip
                        {...getTagProps({ index })}
                        label={option}
                        color="primary"
                        size="small"
                        sx={{
                          backgroundColor: "#ffce5c",
                          color: "black",
                          fontWeight: "bold",
                          margin: "2px",
                        }}
                      />
                    ));
                  }}
                />
              </div>
              <div className="col-span-12 space-y-1">
                <label className="block text-sm font-medium">
                  {t("item.fields.description")}
                </label>
                <TextField
                  name="description"
                  value={updatedItem?.description || ""}
                  onChange={(e) => OnchangeDescription(e.target.value)}
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={10}
                />
              </div>
              <div className="col-span-3 space-y-1 pl-2">
                <label className="block text-sm font-medium">
                  {t("item.fields.price")}
                </label>
                <Input
                  type="number"
                  min={0}
                  name="price"
                  placeholder={t("item.fields.price")}
                  value={updatedItem?.price || ""}
                  onChange={handleInputChange}
                  className="w-full py-2 px-3"
                />
              </div>
              <div className="col-span-3 space-y-1 pl-2">
                <label className="block text-sm font-medium">
                  {t("item.fields.vat")}
                </label>
                <select
                  name="vat"
                  value={updatedItem?.vat || ""}
                  onChange={handleInputChange}
                  className="w-full py-2 px-3 border rounded"
                >
                  <option value="0">0%</option>
                  <option value="15">15%</option>
                </select>
              </div>
            </Box>
            <Box className="flex flex-col items-center">
              <input
                type="file"
                onChange={handleFileChange}
                className="hidden"
                ref={fileInputRef}
              />
              <img
                src={
                  updatedItem?.imagePreview ||
                  item?.image?.url ||
                  "https://via.placeholder.com/300"
                }
                alt={item.name}
                className="w-full max-w-md rounded-lg shadow cursor-pointer"
                onClick={handleImageClick}
              />
            </Box>
          </Box>

          <Box>
            <h3 className="text-lg font-medium">{t("attachments.title")}</h3>
            {updatedItem?.attachments?.map((attachment: any, index: number) => (
              <Box key={index} className="flex items-center gap-4 mb-2">
                {attachment.filepath ? (
                  <Link
                    to={attachment.filepath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 underline"
                    style={{ minWidth: 150 }}
                  >
                    {t("actions.uploadFile")}
                  </Link>
                ) : (
                  <input
                    type="file"
                    onChange={(e) =>
                      e.target.files &&
                      handleAttachmentChange(index, "file", e.target.files[0])
                    }
                    className="flex-grow p-2 border rounded"
                    style={{ width: 150 }}
                  />
                )}
                <Input
                  type="text"
                  placeholder={t("attachments.fileName")}
                  value={attachment.name || ""}
                  onChange={(e) =>
                    handleAttachmentChange(index, "name", e.target.value)
                  }
                  className="flex-grow p-2 border rounded"
                />
                <TextField
                  placeholder={t("attachments.fileDesc")}
                  size="small"
                  value={attachment.description || ""}
                  onChange={(e) =>
                    handleAttachmentChange(index, "description", e.target.value)
                  }
                  fullWidth
                  variant="outlined"
                  className="flex-grow"
                  style={{ minWidth: 250 }}
                />
                <Button
                  color="error"
                  onClick={() => handleAttachmentRemove(index)}
                >
                  {t("actions.delete")}
                </Button>
              </Box>
            ))}
            <Button
              color="primary"
              onClick={handleAttachmentAdd}
              className="flex items-center bg-secondary-brightYellow shadow text-textcolor-black py-2 px-4 text-sm"
              style={{ background: "#000", color: "#fff" }}
            >
              {t("actions.addAttachment")}
            </Button>
          </Box>

          <Box className="mt-6 flex justify-between space-x-4">
            <div></div>
            <Button
              color="primary"
              onClick={handleSave}
              className="flex items-center bg-secondary-brightYellow shadow text-textcolor-black py-2 px-4 text-sm"
              style={{ background: "#000", color: "#fff" }}
              disabled={isLoading}
            >
              {isLoading ? t("item.saving") : t("actions.save")}
            </Button>
          </Box>
        </Box>
      </div>
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ItemPage;
